// Settings → API → Webhooks (086): one endpoint for the whole account, told
// when a submission is received, edited, paid for, moved or deleted.
//
// The signing secret is shown once — when an endpoint is made, and again when
// it is rotated. Everything else about an endpoint can be read back, the log
// of what was sent to it included.
import { useEffect, useMemo, useState } from 'react';
import {
  AccountWebhook, WebhookAttempt, WebhookEventSpec, WebhookInput, createWebhookApi, deleteWebhookApi,
  listWebhookAttemptsApi, listWebhooksApi, replayFailedWebhookApi, replayWebhookAttemptApi, rotateWebhookSecretApi,
  testWebhookApi, updateWebhookApi,
} from '../../api/webhooks';
import { listForms } from '../../api/forms';
import { PlanLimitBody, planLimitOf } from '../../api/limits';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import { useToast } from '../../components/Toast';
import { CodeBlock, Drawer, Modal, RowMenu, TableCard } from '../../components/fs';
import WebhookReference from './WebhookReference';
import { DEFAULT_CONTRACT, WebhookContract, scheduleSpan } from './webhookDocs';
import { PHONE, useMediaQuery } from '../../shared/media';

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

/** How an endpoint stands, in a pill. */
function statusOf(w: AccountWebhook): { tone: string; text: string } {
  if (w.enabled) return w.failure_streak > 0 ? { tone: 'warn', text: 'Failing' } : { tone: 'ok', text: 'On' };
  return w.disabled_at ? { tone: 'danger', text: 'Switched off — kept failing' } : { tone: 'neutral', text: 'Off' };
}

const ATTEMPT_PILL: Record<WebhookAttempt['status'], { tone: string; text: string }> = {
  success: { tone: 'ok', text: 'Delivered' },
  pending: { tone: 'info', text: 'Waiting' },
  failed: { tone: 'danger', text: 'Refused' },
  dead: { tone: 'danger', text: 'Gave up' },
};

const scopeText = (w: AccountWebhook) =>
  w.scope.kind === 'all' ? 'All forms' : `${w.scope.formIds.length} form${w.scope.formIds.length === 1 ? '' : 's'}`;

interface Draft extends WebhookInput { id: number | null }
const blank = (): Draft => ({ id: null, name: '', url: '', events: ['submission.created'], scope: { kind: 'all' } });

export default function WebhookSettings() {
  const toast = useToast();
  const phone = useMediaQuery(PHONE);
  const [hooks, setHooks] = useState<AccountWebhook[] | null>(null);
  const [catalog, setCatalog] = useState<WebhookEventSpec[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [limits, setLimits] = useState({ max: 10, keepDays: 30 });
  const [contract, setContract] = useState<WebhookContract>(DEFAULT_CONTRACT);
  const [error, setError] = useState('');
  const [limitHit, setLimitHit] = useState<PlanLimitBody | null>(null);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftError, setDraftError] = useState('');
  const [forms, setForms] = useState<{ id: number; name: string }[] | null>(null);
  const [secret, setSecret] = useState<{ name: string; secret: string; rotated: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AccountWebhook | null>(null);
  const [confirmRotate, setConfirmRotate] = useState<AccountWebhook | null>(null);
  const [log, setLog] = useState<{ hook: AccountWebhook; attempts: WebhookAttempt[] | null; next?: number; open: number | null } | null>(null);

  const load = () => {
    listWebhooksApi()
      .then((r) => {
        setHooks(r.webhooks || []);
        setCatalog(r.catalog || []);
        setEnabled(r.webhooksEnabled !== false);
        setLimits({ max: r.max ?? 10, keepDays: r.keepDays ?? 30 });
        setContract({ ...DEFAULT_CONTRACT, ...(r.contract || {}) });
      })
      .catch((e) => { setHooks([]); setError(e.message || 'Could not load the webhooks.'); });
  };
  useEffect(load, []);

  // The forms, the first time somebody asks to choose among them.
  const wantsForms = draft?.scope.kind === 'forms';
  useEffect(() => {
    if (!wantsForms || forms) return;
    listForms().then((r) => setForms((r.forms || []).map((f) => ({ id: f.id, name: f.name })))).catch(() => setForms([]));
  }, [wantsForms, forms]);

  const groups = useMemo(() => {
    const out: { group: string; events: WebhookEventSpec[] }[] = [];
    for (const e of catalog) {
      const g = out.find((x) => x.group === e.group);
      if (g) g.events.push(e); else out.push({ group: e.group, events: [e] });
    }
    return out;
  }, [catalog]);

  const openLog = (hook: AccountWebhook) => {
    setLog({ hook, attempts: null, open: null });
    listWebhookAttemptsApi(hook.id)
      .then((r) => setLog((l) => (l && l.hook.id === hook.id ? { ...l, attempts: r.attempts, next: r.nextBefore } : l)))
      .catch((e) => { setLog(null); setError(e.message || 'Could not load the deliveries.'); });
  };
  const moreLog = () => {
    if (!log?.next) return;
    const id = log.hook.id;
    listWebhookAttemptsApi(id, log.next)
      .then((r) => setLog((l) => (l && l.hook.id === id ? { ...l, attempts: [...(l.attempts || []), ...r.attempts], next: r.nextBefore } : l)))
      .catch(() => undefined);
  };

  /** One action on one endpoint: busy while it runs, the list re-read after, its failure said. */
  async function act(run: () => Promise<unknown>, done?: string) {
    setBusy(true); setError(''); setMenu(null);
    try { await run(); if (done) toast(done); load(); }
    catch (e: any) { setError(e.message || 'That did not work.'); }
    finally { setBusy(false); }
  }

  async function save() {
    if (!draft) return;
    setBusy(true); setDraftError('');
    const body: WebhookInput = { name: draft.name.trim(), url: draft.url.trim(), events: draft.events, scope: draft.scope };
    try {
      if (draft.id == null) {
        const r = await createWebhookApi(body);
        setCopied(false);
        setSecret({ name: r.webhook.name || r.webhook.url, secret: r.secret, rotated: false });
      } else {
        await updateWebhookApi(draft.id, body);
        toast('Webhook saved.');
      }
      setDraft(null); load();
    } catch (e: any) {
      const limit = planLimitOf(e);
      if (limit) { setLimitHit(limit); setDraft(null); }
      else setDraftError(e.message || 'Could not save the webhook.');
    } finally { setBusy(false); }
  }

  async function test(hook: AccountWebhook) {
    setBusy(true); setError(''); setMenu(null);
    try {
      const r = await testWebhookApi(hook.id);
      const a = r.attempt;
      if (a?.status === 'success') toast(`Test event delivered — the endpoint answered ${a.response?.status ?? 200}.`);
      else toast(`The test event was not accepted: ${a?.last_error || 'no answer'}`, { kind: 'error' });
      load();
    } catch (e: any) { setError(e.message || 'Could not send the test event.'); }
    finally { setBusy(false); }
  }

  const toggleEvent = (type: string) =>
    setDraft((d) => d && ({ ...d, events: d.events.includes(type) ? d.events.filter((t) => t !== type) : [...d.events, type] }));
  const toggleForm = (id: number) =>
    setDraft((d) => {
      if (!d || d.scope.kind !== 'forms') return d;
      const ids = d.scope.formIds.includes(id) ? d.scope.formIds.filter((x) => x !== id) : [...d.scope.formIds, id];
      return { ...d, scope: { kind: 'forms', formIds: ids } };
    });

  const draftOk = !!draft && /^https:\/\/.+/i.test(draft.url.trim()) && draft.events.length > 0
    && (draft.scope.kind === 'all' || draft.scope.formIds.length > 0);

  const menuFor = (w: AccountWebhook) => (
    <RowMenu open={menu === w.id} onToggle={(o) => setMenu(o ? w.id : null)} label={w.name || w.url}>
      <button onClick={() => { setMenu(null); setDraftError(''); setDraft({ id: w.id, name: w.name, url: w.url, events: w.events, scope: w.scope }); }}>Edit…</button>
      <button onClick={() => { setMenu(null); openLog(w); }}>Deliveries</button>
      <button disabled={busy} onClick={() => void test(w)}>Send test event</button>
      <div className="rule" />
      <button disabled={busy} onClick={() => void act(() => updateWebhookApi(w.id, { enabled: !w.enabled }), w.enabled ? 'Webhook switched off.' : 'Webhook switched on.')}>
        {w.enabled ? 'Switch off' : 'Switch on'}
      </button>
      <button onClick={() => { setMenu(null); setConfirmRotate(w); }}>New signing secret…</button>
      <div className="rule" />
      <button className="danger" onClick={() => { setMenu(null); setConfirmDelete(w); }}>Delete…</button>
    </RowMenu>
  );

  const list = hooks || [];
  const full = list.length >= limits.max;

  return (
    <>
      <TableCard
        className="wh-card"
        title="Webhooks"
        hint={<>One address for the whole account: we send it a signed message when a submission is received, edited, paid for, signed or deleted, when a workflow step fails, and as a follow-up link is sent, opened and completed. A form's own webhook step is separate, and is set on that form's Workflow.</>}
        action={enabled && (
          <button className="fs-btn" disabled={full} title={full ? `An account can have ${limits.max} endpoints.` : undefined}
            onClick={() => { setDraftError(''); setDraft(blank()); }}>Add endpoint</button>
        )}
        footer={list.length > 0 ? (
          <span className="st-muted">
            An endpoint is tried {contract.tries} times over {scheduleSpan(contract.retrySeconds)}. One that keeps failing is switched off and the account's owner is emailed. Deliveries are kept {limits.keepDays} days.
          </span>
        ) : undefined}
      >
        {error && <div className="ad-err wh-pad">{error}</div>}
        {limitHit && <div className="wh-pad"><PlanLimitBlock body={limitHit} /></div>}
        {!enabled && !limitHit && (
          <div className="wh-pad">
            <div className="fs-consequence">
              Account webhooks are not part of the current plan. {list.length > 0
                ? 'The endpoints below stay listed but are sent nothing; upgrading turns them back on.'
                : 'Upgrading adds them.'}
            </div>
          </div>
        )}
        {!hooks ? (
          <div className="st-card-body st-muted">Loading…</div>
        ) : list.length === 0 ? (
          <div className="st-card-body st-muted">No webhook endpoints yet.</div>
        ) : phone ? (
          <div className="api-cards">
            {list.map((w) => {
              const s = statusOf(w);
              return (
                <div className="api-card wh-phone" key={w.id}>
                  <div className="head">
                    <span className="name">{w.name || 'Unnamed endpoint'}</span>
                    {menuFor(w)}
                  </div>
                  <div className="key">{w.url}</div>
                  <div className="when">
                    <span className={`fs-pill ${s.tone}`}>{s.text}</span>{' '}
                    {w.events.length} event{w.events.length === 1 ? '' : 's'} · {scopeText(w)} · last delivered {when(w.last_delivery_at)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <table className="fs-table">
            <thead><tr><th>Endpoint</th><th>Events</th><th>Forms</th><th>Status</th><th>Last delivered</th><th className="fs-row-actions" /></tr></thead>
            <tbody>
              {list.map((w) => {
                const s = statusOf(w);
                return (
                  <tr key={w.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{w.name || 'Unnamed endpoint'}</div>
                      <code className="st-mono wh-url">{w.url}</code>
                    </td>
                    <td>{w.events.length}</td>
                    <td>{scopeText(w)}</td>
                    <td>
                      <span className={`fs-pill ${s.tone}`} title={w.disabled_reason || undefined}>{s.text}</span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }} className="st-muted">{when(w.last_delivery_at)}</td>
                    <td className="fs-row-actions">{menuFor(w)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </TableCard>

      {hooks && <WebhookReference catalog={catalog} contract={contract} />}

      {draft && (
        <Modal
          title={draft.id == null ? 'Add a webhook endpoint' : 'Edit webhook endpoint'}
          onClose={() => setDraft(null)}
          footer={<>
            <button className="fs-btn primary" disabled={busy || !draftOk} onClick={() => void save()}>{draft.id == null ? 'Add endpoint' : 'Save'}</button>
            <button className="fs-btn neutral" onClick={() => setDraft(null)}>Cancel</button>
          </>}
        >
          {draftError && <div className="ad-err">{draftError}</div>}
          <div className="ad-field"><label htmlFor="wh-url">Send to</label>
            <input id="wh-url" className="fs-input" type="url" autoFocus placeholder="https://example.com/hooks/form-magic"
              value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} />
            <small>An https address that is reachable from the internet.</small>
          </div>
          <div className="ad-field"><label htmlFor="wh-name">Name</label>
            <input id="wh-name" className="fs-input" placeholder="e.g. Warehouse sync" maxLength={80}
              value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
          <fieldset className="wh-set">
            <legend>Forms</legend>
            <label className="fs-check">
              <input type="radio" name="wh-scope" checked={draft.scope.kind === 'all'} onChange={() => setDraft({ ...draft, scope: { kind: 'all' } })} />
              <span><b>All forms</b><small>Including forms made later.</small></span>
            </label>
            <label className="fs-check">
              <input type="radio" name="wh-scope" checked={draft.scope.kind === 'forms'} onChange={() => setDraft({ ...draft, scope: { kind: 'forms', formIds: [] } })} />
              <span><b>Only these forms</b></span>
            </label>
            {draft.scope.kind === 'forms' && (
              <div className="wh-forms">
                {!forms ? <span className="st-muted">Loading…</span>
                  : forms.length === 0 ? <span className="st-muted">There are no forms yet.</span>
                  : forms.map((f) => (
                    <label className="fs-check" key={f.id}>
                      <input type="checkbox" checked={draft.scope.kind === 'forms' && draft.scope.formIds.includes(f.id)} onChange={() => toggleForm(f.id)} />
                      <span>{f.name}</span>
                    </label>
                  ))}
              </div>
            )}
          </fieldset>
          {groups.map((g) => (
            <fieldset className="wh-set" key={g.group}>
              <legend>{g.group}</legend>
              {g.events.map((e) => (
                <label className="fs-check" key={e.type}>
                  <input type="checkbox" checked={draft.events.includes(e.type)} onChange={() => toggleEvent(e.type)} />
                  <span><b>{e.label}</b><small>{e.description} <code>{e.type}</code></small></span>
                </label>
              ))}
            </fieldset>
          ))}
        </Modal>
      )}

      {secret && (
        <Modal title={secret.rotated ? 'New signing secret' : `Endpoint added: ${secret.name}`} narrow onClose={() => setSecret(null)}
          footer={<button className="fs-btn primary" onClick={() => setSecret(null)}>Done</button>}>
          <p className="wh-note">
            This is the endpoint's signing secret. Copy it now — <b>this is the only time it will be shown.</b>
            {secret.rotated && ' The old secret has stopped working.'}
          </p>
          <div className="wh-secret">
            <code>{secret.secret}</code>
            <button className="fs-btn" onClick={() => {
              navigator.clipboard?.writeText(secret.secret).then(() => setCopied(true)).catch(() => undefined);
            }}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
        </Modal>
      )}

      {confirmRotate && (
        <Modal title="Make a new signing secret?" narrow onClose={() => setConfirmRotate(null)}
          footer={<>
            <button className="fs-btn primary" disabled={busy} onClick={() => {
              const w = confirmRotate;
              setConfirmRotate(null);
              void act(async () => {
                const r = await rotateWebhookSecretApi(w.id);
                setCopied(false);
                setSecret({ name: w.name || w.url, secret: r.secret, rotated: true });
              });
            }}>Make a new secret</button>
            <button className="fs-btn neutral" onClick={() => setConfirmRotate(null)}>Cancel</button>
          </>}>
          <p className="wh-note">
            The current secret stops working at once: <b>{confirmRotate.name || confirmRotate.url}</b> will fail its signature
            check until it has the new one.
          </p>
        </Modal>
      )}

      {confirmDelete && (
        <Modal title="Delete this endpoint?" narrow onClose={() => setConfirmDelete(null)}
          footer={<>
            <button className="fs-btn danger" disabled={busy} onClick={() => {
              const w = confirmDelete;
              setConfirmDelete(null);
              void act(() => deleteWebhookApi(w.id), 'Webhook deleted.');
            }}>Delete endpoint</button>
            <button className="fs-btn neutral" onClick={() => setConfirmDelete(null)}>Cancel</button>
          </>}>
          <p className="wh-note">
            <b>{confirmDelete.name || confirmDelete.url}</b> will be sent nothing further, and its delivery log goes with it.
          </p>
        </Modal>
      )}

      {log && (
        <Drawer title={`Deliveries — ${log.hook.name || log.hook.url}`} onClose={() => setLog(null)}
          footer={log.attempts?.some((a) => a.status === 'failed' || a.status === 'dead') ? (
            <button className="fs-btn" disabled={busy} onClick={() => {
              const hook = log.hook;
              void act(async () => { const r = await replayFailedWebhookApi(hook.id); toast(`${r.queued} queued to send again.`); openLog(hook); });
            }}>Replay all failed</button>
          ) : undefined}>
          {!log.attempts ? <p className="st-muted">Loading…</p>
            : log.attempts.length === 0 ? <p className="st-muted">Nothing has been sent to this endpoint yet.</p>
            : (
              <ul className="wh-log">
                {log.attempts.map((a) => {
                  const p = ATTEMPT_PILL[a.status];
                  const open = log.open === a.id;
                  return (
                    <li key={a.id}>
                      <button type="button" className="wh-log-row" aria-expanded={open}
                        onClick={() => setLog({ ...log, open: open ? null : a.id })}>
                        <span className={`fs-pill ${p.tone}`}>{p.text}</span>
                        <code>{a.type}</code>
                        <span className="when">{when(a.updated_at)}</span>
                      </button>
                      {open && (
                        <div className="wh-log-detail">
                          <div className="st-muted">
                            {a.attempts} {a.attempts === 1 ? 'try' : 'tries'}
                            {a.response?.status ? ` · answered ${a.response.status}` : ''}
                            {a.status === 'pending' && a.attempts > 0 ? ` · next try ${when(a.next_attempt_at)}` : ''}
                            {' · '}<code>{a.event_id}</code>
                          </div>
                          {a.last_error && <div className="wh-log-err">{a.last_error}</div>}
                          <CodeBlock code={JSON.stringify(a.payload, null, 2)} label="What was sent" />
                          {a.status !== 'pending' && (
                            <button className="fs-btn sm" disabled={busy} onClick={() => {
                              const hook = log.hook;
                              void act(async () => { await replayWebhookAttemptApi(a.id); openLog(hook); }, 'Queued to send again.');
                            }}>Send again</button>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          {log.next && <button className="fs-btn text" onClick={moreLog}>Show older</button>}
        </Drawer>
      )}
    </>
  );
}
